import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { AdmissionApplication } from "@/commons/services/Waiters";
import { ResidentStatus } from "@/commons/services/Residents";

/**
 * ### 待機者経過履歴 API 操作クラス
 */
export default class WaiterProgressHistories {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET WaiterProgressHistories
   */
  public async get(userStore: LoginUser, residentIdInSystem: number, historyId: number): Promise<WaiterProgressHistoriesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentIdInSystem,
      historyId: historyId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterProgressHistory, formatParams);

    const response = await this.commonService.executeGet<WaiterProgressHistoriesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET WaiterProgressHistories
   */
  public async gets(userStore: LoginUser, residentIdInSystem: number, queryParams?: { startIndex?: number; endIndex?: number }): Promise<Array<WaiterProgressHistoriesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentIdInSystem
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterProgressHistories, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<WaiterProgressHistoriesGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET WaiterProgressHistoriesCount
   */
  public async getCount(userStore: LoginUser, residentIdInSystem: number): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentIdInSystem
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterProgressHistories, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST WaiterProgressHistories
   */
  public async post(userStore: LoginUser, residentIdInSystem: number, requestBody: WaiterProgressHistoriesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentIdInSystem
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterProgressHistories, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH WaiterProgressHistories
   */
  public async patch(userStore: LoginUser, residentIdInSystem: number, historyId: number, requestBody: WaiterProgressHistoriesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentIdInSystem,
      historyId: historyId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterProgressHistory, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### DELETE WaiterProgressHistories
   */
  public async delete(userStore: LoginUser, residentIdInSystem: number, historyId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      residentId: residentIdInSystem,
      historyId: historyId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterProgressHistory, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface WaiterProgressHistoriesGetModel {
  /** システムで一意の利用者ID */
  residentIdInSystem: number;
  /** システムで一意のID */
  id: number;
  /** 施設ID */
  facilityId: number;
  /** 履歴種別(1：経過記録、2：情報更新履歴) */
  historyType: HistoryType;
  /** 日時 */
  datetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string | null;
  /** 本文のみの履歴ではなく、入居者の状態変更履歴を残す場合に使用する */
  beforeStatus: ResidentStatus | null;
  /** 本文のみの履歴ではなく、入居者の状態変更履歴を残す場合に使用する */
  afterStatus: ResidentStatus | null;
  /** 本文のみの履歴ではなく、入居者の情報更新履歴を残す場合に使用する */
  beforeValue: AdmissionApplication | null;
  /** 本文のみの履歴ではなく、入居者の情報更新履歴を残す場合に使用する */
  afterValue: AdmissionApplication | null;
  /** 記載者の種別(1：施設ユーザー、2：待機者ユーザー) */
  writerType: WriterType;
  /** 記載者種別に応じて可変(1：「施設ユーザー名」、2：「申込者」) */
  writerName: string;
  /** 記載者種別に応じて可変(1：施設ユーザー ID、2：申込者のユーザーのソートキー) */
  writerId: string;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export enum HistoryType {
  PROGRESS_HISTORIES = 1,
  UPDATE_HISTORY = 2
}

export enum WriterType {
  FACILITY_USER = 1,
  WAITER = 2
}

export interface WaiterProgressHistoriesPostModel {
  /** 施設ID */
  facilityId: number;
  /** 日時 */
  datetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
}

export interface WaiterProgressHistoriesPatchModel {
  /** 施設ID */
  facilityId: number;
  /** 日時 */
  datetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 更新日時 */
  updated: number;
}
